// ============================================================
//  qadder.sa — Supabase Client v2
//  متوافق مع schema v2 بالكامل
// ============================================================

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

// client عام — للمستخدمين العاديين
export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// client الأدمن — نفس العميل العادي. صلاحيات الأدمن تُفرض عبر RLS (public.is_admin())
// ⚠️ لا تضع مفتاح service_role في أي متغير يبدأ بـ REACT_APP_ — ينكشف في المتصفح
export const supabaseAdmin = supabase;


// ─────────────────────────────────────────────
//  AUTH — المصادقة (Supabase Phone OTP)
// ─────────────────────────────────────────────

// تسجيل الخروج
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

// المستخدم الحالي
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// الاستماع لتغييرات الـ Auth
export const onAuthChange = (callback) => {
  return supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  });
};


// ─────────────────────────────────────────────
//  ADMIN ROLES — أدمن مقيّد (رفع الأسئلة فقط)
//  جدول questions_admins — يُدار من لوحة الأدمن
// ─────────────────────────────────────────────

// يُستخدم وقت تسجيل الدخول (client عام) — يتحقق هل رقم الجوال له صلاحية مقيّدة
export const isQuestionsAdminPhone = async (phone) => {
  const clean = (phone || '').replace(/\s/g, '');
  if (!clean) return false;
  const { data } = await supabase.from('questions_admins').select('phone');
  return (data || []).some(row => clean.endsWith((row.phone || '').replace(/^\+966|^966|^0/, '')));
};

// إدارة القائمة — من لوحة الأدمن فقط (client الأدمن)
export const listQuestionsAdmins = async () => {
  const { data } = await supabaseAdmin.from('questions_admins').select('*').order('created_at', { ascending: false });
  return data || [];
};

export const addQuestionsAdmin = async (phone) => {
  const formatted = phone.startsWith('+966') ? phone : '+966' + phone.replace(/^0/, '');
  return supabaseAdmin.from('questions_admins').insert({ phone: formatted });
};

export const removeQuestionsAdmin = async (phone) => {
  return supabaseAdmin.from('questions_admins').delete().eq('phone', phone);
};


// ─────────────────────────────────────────────
//  AUTH SESSIONS — منع الدخول المزدوج
// (جدول auth_sessions — مختلف عن sessions التدريب)
// ─────────────────────────────────────────────

// تسجيل جلسة مصادقة جديدة (عند كل دخول)
export const registerAuthSession = async (userId) => {
  // 1. إلغاء كل الجلسات القديمة
  await supabase.from('auth_sessions').update({ is_active: false }).eq('user_id', userId).eq('is_active', true);

  // 2. إنشاء جلسة جديدة — نستخدم supabaseAdmin لتجاوز RLS
  const sessionToken = crypto.randomUUID();
  const deviceInfo   = navigator.userAgent;

  const { data, error } = await supabase
    .from('auth_sessions')
    .insert({ user_id: userId, session_token: sessionToken, device_info: deviceInfo })
    .select()
    .single();

  if (error) console.error('[registerAuthSession]', error.message);
  else localStorage.setItem('qadder_session_token', sessionToken);

  return { data, error };
};

// التحقق من صحة الجلسة الحالية
export const validateAuthSession = async (userId) => {
  const token = localStorage.getItem('qadder_session_token');
  if (!token) return false;

  const { data } = await supabase
    .from('auth_sessions')
    .select('is_active')
    .eq('user_id', userId)
    .eq('session_token', token)
    .single();

  return data?.is_active === true;
};

// نبضة حية كل 30 ثانية (تحديث last_active)
export const startHeartbeat = (userId) => {
  const token = localStorage.getItem('qadder_session_token');
  if (!token) return null;

  const interval = setInterval(async () => {
    const isValid = await validateAuthSession(userId);
    if (!isValid) {
      // الجلسة انتهت (دخل من جهاز آخر) → أخرجه
      clearInterval(interval);
      localStorage.removeItem('qadder_session_token');
      await supabase.auth.signOut();
      window.location.reload();
    } else {
      await supabase
        .from('auth_sessions')
        .update({ last_active: new Date().toISOString() })
        .eq('session_token', token);
    }
  }, 30000);

  return interval; // احفظه عشان تقدر توقفه عند الخروج
};


// ─────────────────────────────────────────────
//  USERS — المستخدمون
// ─────────────────────────────────────────────

// جلب أو إنشاء مستخدم بعد OTP
export const upsertUser = async (phone) => {
  const { data, error } = await supabase
    .from('users')
    .upsert({ phone, last_login: new Date().toISOString() }, { onConflict: 'phone' })
    .select()
    .single();
  return { data, error };
};

// حفظ المرحلة الدراسية
export const updateUserStage = async (userId, stage) => {
  const { data, error } = await supabase
    .from('users')
    .update({ stage })
    .eq('id', userId)
    .select()
    .single();
  return { data, error };
};

// جلب بيانات المستخدم
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
  return { data, error };
};


// ─────────────────────────────────────────────
//  PLANS — الباقات
// ─────────────────────────────────────────────

export const getPlans = async () => {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('is_active', true)
    .order('price_sar');
  return { data, error };
};


// ─────────────────────────────────────────────
//  ENSURE USER + MERGE — تأكيد وجود المستخدم ونقل الاشتراك
// ─────────────────────────────────────────────

// يُستدعى عند كل تسجيل دخول:
// ١. يضمن وجود صف في public.users بالـ auth.uid الحقيقي
// ٢. ينقل أي اشتراك أدمن مرتبط برقم الجوال (placeholder) للـ ID الحقيقي
export const ensureUserAndMerge = async (authUser) => {
  if (!authUser?.id) return;
  // كل العمل يتم داخل قاعدة البيانات (دالة security definer) — تتحقق من رقم الجوال من جلسة الدخول نفسها
  const { error } = await supabase.rpc('claim_admin_granted_subscriptions');
  if (error) console.error('[ensureUserAndMerge]', error.message);
};


// ─────────────────────────────────────────────
//  SUBSCRIPTIONS — الاشتراكات
// ─────────────────────────────────────────────

// هل المستخدم مشترك حالياً؟
export const checkSubscription = async (userId) => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*, plans(*)')
    .eq('user_id', userId)
    .eq('status', 'active')
    .gt('expires_at', new Date().toISOString())
    .order('expires_at', { ascending: false })   // الأحدث أولاً
    .limit(1)
    .maybeSingle();                              // لا تفشل عند وجود أكثر من صف
  return { data, error };
};

// إنشاء اشتراك بعد الدفع
export const createSubscription = async (userId, planId, paymentId) => {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 30);

  // ألغِ أي اشتراكات نشطة سابقة لهذا المستخدم — حتى لا يتعارض اشتراك قديم
  // ذو تاريخ انتهاء أبعد مع الباقة الجديدة المشتراة فعلياً
  await supabase
    .from('subscriptions')
    .update({ status: 'cancelled' })
    .eq('user_id', userId)
    .eq('status', 'active');

  const { data, error } = await supabase
    .from('subscriptions')
    .insert({
      user_id:    userId,
      plan_id:    planId,
      payment_id: paymentId,                      // ← payment_id
      expires_at: expiresAt.toISOString(),         // ← expires_at
      status:     'active',
    })
    .select()
    .single();
  return { data, error };
};


// ─────────────────────────────────────────────
//  PAYMENTS — المدفوعات
// ─────────────────────────────────────────────

export const createPayment = async (userId, planId, amountSar, moyasarId) => {
  const { data, error } = await supabase
    .from('payments')
    .insert({
      user_id:        userId,
      plan_id:        planId,
      amount_halalas: amountSar * 100,
      moyasar_id:     moyasarId,
      status:         'paid',
      paid_at:        new Date().toISOString(),
    })
    .select()
    .single();
  return { data, error };
};


// ─────────────────────────────────────────────
//  TESTS & SECTIONS — الاختبارات والأقسام
// ─────────────────────────────────────────────

// جميع الاختبارات مع أقسامها
export const getTests = async () => {
  const { data, error } = await supabase
    .from('tests')
    .select('*, sections(*)');
  return { data, error };
};

// أقسام اختبار معين
export const getSections = async (testId) => {
  const { data, error } = await supabase
    .from('sections')
    .select('*')
    .eq('test_id', testId);
  return { data, error };
};


// ─────────────────────────────────────────────
//  QUESTIONS — الأسئلة
// ─────────────────────────────────────────────

// أسئلة قسم معين
export const getQuestions = async (sectionId, limit = 20) => {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('section_id', sectionId)
    .eq('is_active', true)
    .limit(limit);
  return { data, error };
};

// تعيين النوع → قيم عمود subject في جدول questions
const MODE_TO_SUBJECTS = {
  'كمي':      ['كمي'],
  'لفظي':     ['لفظي'],
  'تحصيلي':   ['رياضيات', 'فيزياء', 'كيمياء', 'أحياء', 'علوم', 'عربي', 'تاريخ'],
  'step':     ['قراءة', 'مفردات', 'قواعد'],
  'tajamuat': ['كمي', 'لفظي', 'رياضيات', 'فيزياء', 'كيمياء', 'أحياء'],
};

// جلب أسئلة حسب نوع التدريب (كمي / لفظي / تحصيلي / step)
// كشف نص تالف — يحتوي على emoji أو أحرف غير مقروءة وسط نص عربي
const hasCorruptedText = (s, isQuestion = false) => {
  // أي emoji في نص السؤال = تالف (السؤال ليس من المفترض أن يحتوي emoji)
  // في الخيارات: emoji واحد مقبول (مثل ✓)، اثنان أو أكثر = تالف
  const emojiPattern = /[\u{1F300}-\u{1FFFF}\u{2600}-\u{27BF}\u{1F000}-\u{1FFFF}]/gu;
  const emojiCount = (s.match(emojiPattern) || []).length;
  if (isQuestion && emojiCount >= 1) return true;
  if (!isQuestion && emojiCount >= 2) return true;
  // أحرف replacement أو علامات استفهام مربعة (تشير لترميز خاطئ)
  if (/�|■|□|◆|◇|⬥|⬦/.test(s)) return true;
  return false;
};

const isValidOption = (o) => {
  // إزالة أحرف Unicode غير مرئية قبل الفحص
  const s = String(o || '').replace(/[​-‏‪-‮⁠﻿]/g, '').trim();
  const low = s.toLowerCase();
  return (
    s.length > 2 &&
    !/^[.\s،,…\-_–—]+$/.test(s) &&
    !/^-{2,}$/.test(s) &&
    !/^\.{2,}$/.test(s) &&
    low !== 'blank' &&
    low !== 'null' &&
    low !== 'undefined' &&
    low !== 'n/a' &&
    low !== 'none' &&
    low !== 'na' &&
    s !== '-' &&
    !hasCorruptedText(s)
  );
};

// يشمل أسئلة الفقرات والنصوص بأشكالها المختلفة
const needsPassage = (t) => {
  const text = t || '';
  // كلمات دالة على فقرة أو نص
  if (/فقرة|وفق[اً]?|النص|للنص|بالنص|المقطع|القطعة|الجزء/.test(text)) return true;
  // "العبارة" بأي شكل = سؤال يشير لنص
  if (/العبارة|عبارة/.test(text)) return true;
  // يقصد الكاتب / الكاتب / السياق
  if (/يقصد الكاتب|الكاتب يرى|حسب الكاتب|يدل السياق/.test(text)) return true;
  // أسئلة مرجعية الضمير — تحتاج سياق نص
  if (/يعود.*(?:الضمير|على|إلى)|الضمير.*يعود|الضمير.*في.*كلمة/.test(text)) return true;
  // السؤال يبدأ برقم عربي أو لاتيني (بند من فقرة مرقّمة مثل: 79. النهار...)
  if (/^\s*[\d٠-٩]+\s*[.)،]/.test(text)) return true;
  // السؤال ينتهي برقم — مرجع لبند في فقرة
  if (/\s\d+\s*$/.test(text.trim())) return true;
  // سؤال إكمال فراغات — يحتوي نقاط متعددة بدل كلمة
  if (/\.{3,}/.test(text)) return true;
  return false;
};

// أسئلة تحتاج صورة/شكل/جدول — تُحذف إن لم يكن عندها image_url
const needsImage = (t) =>
  /الشكل|الرسم البياني|المخطط البياني|المخطط|البياني التالي|البياني الآتي|المجاور|الصورة التالية|الشكل التالي|الجدول التالي|الجدول الآتي|الشكل الآتي|أدناه|انظر الشكل|انظر الرسم/.test(t || '');

// أسئلة ناقصة السياق — تحتاج جدول أو معطيات مسبقة
const needsContext = (t) => {
  const text = t || '';
  // سؤال يطلب إيجاد قيمة متغير من جدول (ص = ؟ / س = ؟)
  if (/=\s*[؟?]\s*$/.test(text.trim())) return true;
  // سؤال قصير جداً يحتوي فقط على متغير (أوجد قيمة ص)
  if (text.length < 20 && /أوجد.*(قيمة|ناتج|حاصل)/.test(text)) return true;
  // إشارة لمعطيات أو بيانات سابقة
  if (/من البيانات|من المعطيات|من الجدول|استناداً|البيانات السابقة|المعطيات التالية/.test(text)) return true;
  // إذا كانت س = رقم فكم قيمة ع؟ — يحتاج دالة أو علاقة معرّفة في سياق سابق
  if (/إذا كان[ت]?\s+[سصعغضطحيمنلخ]\s*=\s*[\d٠-٩]/.test(text)) return true;
  // أوجد الفرق/المجموع بين اليوم الثالث والسادس — يحتاج جدول بيانات أيام
  if (/اليوم\s+ال(أول|ثاني|ثالث|رابع|خامس|سادس|سابع|ثامن|تاسع|عاشر)/.test(text)) return true;
  // سؤال عن نسبة/عدد شيء محدد بدون سياق — "ما نسبة X" / "كم عدد X" بلا مقدمة
  if (/^ما\s+نسبة\s+\S/.test(text.trim()) && text.length < 40) return true;
  if (/^كم\s+(عدد|نسبة|مقدار)\s+\S/.test(text.trim()) && text.length < 40) return true;
  // يشير لـ "الأول/الثاني/الثالث" بدون ذكر مرجع — يحتاج جدول أو قائمة
  if (/(?:الغير|غير)\s+(?:الأول|الثاني|الثالث|الرابع|الخامس)/.test(text)) return true;
  // "ما نسبة ... الثالثة/الأولى" — يشير لترتيب في جدول غير مرئي
  if (/نسبة.+(?:الأول[ةه]?|الثاني[ةه]?|الثالث[ةه]?|الرابع[ةه]?|الخامس[ةه]?)/.test(text)) return true;
  return false;
};

// خيارات مكررة — إذا كان أكثر من خيارين متطابقين = سؤال فاسد
const hasDuplicateOptions = (opts) => {
  const normalized = opts.map(o => String(o || '').trim().toLowerCase());
  const unique = new Set(normalized);
  return unique.size < 3; // يُقبل خياران مختلفان كحد أدنى، لكن 3 متطابقة = فاسد
};

const isValidQuestion = (q) => {
  const text = (q.question_ar || '').trim();
  const opts = Array.isArray(q.options) ? q.options : [];
  const requiresImage = needsImage(text);
  return (
    text.length >= 15 &&
    opts.length === 4 &&
    !hasCorruptedText(text, true) &&
    opts.every(isValidOption) &&
    !hasDuplicateOptions(opts) &&
    !needsPassage(text) &&
    !needsContext(text) &&
    (!requiresImage || !!q.image_url)
  );
};

export const getQuestionsByMode = async (mode, limit = 20, subject = null, seenIds = []) => {
  const seenSet = new Set(seenIds);
  const isValid = isValidQuestion;

  // تحديد المواد المطلوبة
  let subjects;
  if (subject && typeof subject === 'object' && subject.subjects) {
    subjects = subject.subjects;
    subject = subject.subject;
  } else if (subject && subject !== 'الكل') {
    subjects = [subject];
  } else {
    subjects = MODE_TO_SUBJECTS[mode] || [mode];
  }

  // جلب الأسئلة مباشرة بعمود subject
  let query = supabase
    .from('questions')
    .select('*')
    .in('subject', subjects)
    .eq('is_active', true)
    .limit(500);

  const { data, error } = await query;

  const allValid = (data || []).filter(isValid);
  const total    = allValid.length;
  const unseen   = allValid.filter(q => !seenSet.has(q.id));
  const pool     = unseen.length >= limit ? unseen : allValid;
  const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, limit);

  return { data: shuffled, error, total };
};

// جلب test_id حسب النوع (للجلسة)
export const getTestIdByMode = async (mode) => {
  // نجرب أسماء النوع بالعربي والإنجليزي
  const tryTypes = {
    'كمي':    ['qudurat', 'قدرات', 'qudraat', 'kudrat'],
    'لفظي':   ['qudurat', 'قدرات', 'qudraat', 'kudrat'],
    'تحصيلي': ['tahsili', 'تحصيلي'],
    'step':   ['step'],
  };
  const candidates = tryTypes[mode] || [mode];

  for (const type of candidates) {
    const { data } = await supabase
      .from('tests')
      .select('id')
      .eq('type', type)
      .limit(1)          // ← منع خطأ "multiple rows" لو أكثر من صف بنفس النوع
      .maybeSingle();
    if (data?.id) return data.id;
  }

  // آخر محاولة: أي اختبار موجود في الجدول
  const { data: fallback } = await supabase
    .from('tests')
    .select('id')
    .limit(1)
    .maybeSingle();
  return fallback?.id || null;
};


// ─────────────────────────────────────────────
//  SESSIONS — جلسات التدريب
// ─────────────────────────────────────────────

// إنشاء جلسة تدريب جديدة
export const createTrainingSession = async (userId, testId, mode = 'practice') => {
  // نولّد UUID محلياً لتجنّب الاعتماد على SELECT بعد INSERT
  const sessionId = crypto.randomUUID();
  const payload = { id: sessionId, user_id: userId, mode };
  if (testId) payload.test_id = testId;

  const { error } = await supabase
    .from('sessions')
    .insert(payload);

  if (error) {
    console.error('[createSession error]', error.code, error.message, error.details);
    return { data: null, error };
  }
  return { data: { id: sessionId }, error: null };
};

// إنهاء الجلسة وحفظ النتيجة
export const completeSession = async (sessionId, correctCount, totalQuestions, durationSecs) => {
  const score = totalQuestions > 0
    ? Math.round((correctCount / totalQuestions) * 100 * 100) / 100
    : 0;

  // نحذف .select().single() — الـ UPDATE يكفي ولا نحتاج البيانات راجعة
  const { error } = await supabase
    .from('sessions')
    .update({
      correct_count:   correctCount,
      total_questions: totalQuestions,
      score,
      duration_secs:   durationSecs,
      is_completed:    true,
      ended_at:        new Date().toISOString(),
    })
    .eq('id', sessionId);

  if (error) console.error('[completeSession error]', error.code, error.message, error.details);
  return { error };
};

// آخر جلسات المستخدم
export const getUserSessions = async (userId, limit = 10) => {
  const { data, error } = await supabase
    .from('sessions')
    .select('id, mode, score, correct_count, total_questions, duration_secs, ended_at, test_id')
    .eq('user_id', userId)
    .eq('is_completed', true)
    .order('ended_at', { ascending: false })
    .limit(limit);
  if (error) console.error('[getUserSessions]', error);
  return { data, error };
};


// ─────────────────────────────────────────────
//  SESSION ANSWERS — إجابات الجلسة
// ─────────────────────────────────────────────

export const saveAnswer = async (sessionId, questionId, chosenIndex, isCorrect, timeSecs) => {
  const { data, error } = await supabase
    .from('session_answers')
    .insert({
      session_id:   sessionId,
      question_id:  questionId,
      chosen_index: chosenIndex,
      is_correct:   isCorrect,
      time_secs:    timeSecs,
    });
  return { data, error };
};


// ─────────────────────────────────────────────
//  PERFORMANCE — الأداء
// (يُحدَّث تلقائياً عبر Trigger في Supabase)
// ─────────────────────────────────────────────

export const getUserPerformance = async (userId) => {
  // نحسب مباشرة من sessions بدل الاعتماد على trigger
    // نحسب من الإجابات الفعلية — لا من عدد أسئلة الجلسة المخطط لها
  const { data, error } = await supabase
    .from('session_answers')
    .select('is_correct, sessions!inner(user_id, mode)')
    .eq('sessions.user_id', userId)
    .limit(5000);

  if (error || !data?.length) return { data: null, error };

  const { count: sessionCount } = await supabase
    .from('sessions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_completed', true);

  const totalSessions  = sessionCount || 0;
  const totalQuestions = data.length;
  const totalCorrect   = data.filter(r => r.is_correct).length;
  const avgScore       = totalQuestions > 0
    ? Math.round((totalCorrect / totalQuestions) * 100)
    : 0;

  // إحصائيات لكل مادة
  const modeKeys = ['كمي', 'لفظي', 'تحصيلي', 'step'];
  const by_mode = {};
  modeKeys.forEach(m => {
    const rows = data.filter(r => r.sessions?.mode === m);
    if (!rows.length) return;
    const c = rows.filter(r => r.is_correct).length;
    by_mode[m] = {
      sessions: 0,
      questions: rows.length,
      accuracy: Math.round((c / rows.length) * 100),
    };
  });

  return {
    data: {
      total_sessions:  totalSessions,
      total_questions: totalQuestions,
      avg_score:       avgScore,
      by_mode,
    },
    error: null,
  };
};

// ─────────────────────────────────────────────
//  REVIEWS — آراء المستخدمين
// ─────────────────────────────────────────────

export const saveReview = async ({ displayName, stars, comment, stage }) => {
  const { error } = await supabase
    .from('reviews')
    .insert({
      display_name:   displayName || 'مستخدم',
      reviewer_name:  displayName || null,
      stars,
      comment:  comment || null,
      stage:    stage   || null,
      approved: false,   // تنتظر موافقة الأدمن
    });
  return { error };
};

// جلب أسئلة عشوائية للاختبار التجريبي
export const getTrialQuestions = async (sectionId, count = 20) => {
  const { data, error } = await supabase
    .from('questions')
    .select('id, section_id, question_ar, options, correct_key, explanation')
    .eq('section_id', sectionId)
    .limit(500);

  if (error || !data?.length) return { data: [], error };
  const valid = data.filter(isValidQuestion);
  const shuffled = [...valid].sort(() => Math.random() - 0.5).slice(0, count);
  return { data: shuffled, error: null };
};

// ─────────────────────────────────────────────
//  QUESTION REPORTS — الإبلاغ عن مشكلة
// ─────────────────────────────────────────────

export const reportQuestion = async (questionId, userId, reportType, note = '', userPhone = null) => {
  // user_id محذوف عمداً — يسبب FK conflict بين auth.users و public.users
  const payload = {
    report_type: reportType,
    note:        note.trim() || null,
    user_phone:  userPhone || null,
  };
  if (questionId && typeof questionId === 'string' && questionId.length > 10) {
    payload.question_id = questionId;
  }
  const { error } = await supabase
    .from('question_reports')
    .insert(payload);
  if (error) console.error('[reportQuestion error]', error.code, error.message, error.details);
  return { error };
};

export const getQuestionReports = async (limit = 200) => {
  const { data, error } = await supabase
    .from('question_reports')
    .select('*, questions(question_ar, subject)')
    .order('created_at', { ascending: false })
    .limit(limit);
  return { data, error };
};

// ─────────────────────────────────────────────
//  WRONG ANSWERS — الأسئلة الخاطئة
// ─────────────────────────────────────────────

// جلب كل الأسئلة التي أخطأ فيها المستخدم (بدون تكرار)
export const getWrongAnswers = async (userId, limit = 300) => {
  // خطوة 1: جلب session IDs الخاصة بالمستخدم
  const { data: sessionsData, error: sessErr } = await supabase
    .from('sessions')
    .select('id')
    .eq('user_id', userId);

  if (sessErr || !sessionsData?.length) return { data: [], error: sessErr };

  const sessionIds = sessionsData.map(s => s.id);

  // خطوة 2: جلب الإجابات الخاطئة من تلك الجلسات
  const { data, error } = await supabase
    .from('session_answers')
    .select('id, chosen_index, session_id, question_id, questions(id, question_ar, options, correct_key, subject, image_url)')
    .in('session_id', sessionIds)
    .eq('is_correct', false)
    .order('id', { ascending: false })
    .limit(limit);

  if (error || !data) return { data: [], error };

  // إزالة التكرار — نحتفظ بآخر خطأ لكل سؤال
  const seen = new Set();
  const unique = data.filter(a => {
    const qid = a.questions?.id;
    if (!qid || seen.has(qid)) return false;
    seen.add(qid);
    return true;
  });

  return { data: unique, error: null };
};

export const getApprovedReviews = async (limit = 12) => {
  const { data, error } = await supabase
    .from('reviews')
    .select('display_name, stars, comment, created_at')
    .eq('approved', true)
    .order('created_at', { ascending: false })
    .limit(limit);
  return { data, error };
};

// ─────────────────────────────────────────────
//  STREAK — الـ Streak اليومي (Supabase)
// ─────────────────────────────────────────────

// جلب الـ Streak الحالي للمستخدم
export const getStreak = async (userId) => {
  if (!userId) return { current: 0, longest: 0 };
  const { data, error } = await supabase
    .from('user_streaks')
    .select('current_streak, longest_streak, last_activity_date')
    .eq('user_id', userId)
    .single();
  if (error || !data) return { current: 0, longest: 0 };
  return { current: data.current_streak, longest: data.longest_streak, lastDate: data.last_activity_date };
};

// تحديث الـ Streak عند نشاط يومي (يُستدعى عند فتح الصفحة أو إنهاء جلسة)
export const updateStreak = async (userId) => {
  if (!userId) return { current: 0, longest: 0 };

  const today     = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  // جلب السجل الحالي
  const { data: existing } = await supabase
    .from('user_streaks')
    .select('current_streak, longest_streak, last_activity_date')
    .eq('user_id', userId)
    .single();

  let current, longest;

  if (!existing) {
    // أول مرة
    current = 1; longest = 1;
  } else if (existing.last_activity_date === today) {
    // نفس اليوم — لا تغيير
    return { current: existing.current_streak, longest: existing.longest_streak };
  } else if (existing.last_activity_date === yesterday) {
    // يوم متتالي
    current = existing.current_streak + 1;
    longest = Math.max(current, existing.longest_streak);
  } else {
    // انقطع الـ Streak
    current = 1;
    longest = existing.longest_streak || 1;
  }

  await supabase.from('user_streaks').upsert({
    user_id: userId,
    current_streak: current,
    longest_streak: longest,
    last_activity_date: today,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id' });

  return { current, longest };
};
